import { UseMutationResult, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useControllerService } from 'data/utils/hooks';
import { useQuery, useMutation } from 'data/utils/useQuery';
import { API_VERSION_V1, QueryKeys } from 'data/constants';
import { type RightDto, type UserDto, UserDtoWithAdminOption } from '../types';

const USERS_API_PATHS = {
  v1: {
    getUsersPath: 'v1/users',
    getUserPath: 'v1/user',
    getSpecifiedUserPath: 'v1/user',
    createUserPath: 'v1/user/create',
    revokeUserRightsPath: 'v1/user/rights/revoke',
    deleteUserPath: 'v1/user/delete',
  },
  v2: {
    getUsersPath: 'v2/users',
    getUserPath: 'v2/user',
    getSpecifiedUserPath: 'v2/user',
    createUserPath: 'v2/users',
    deleteUserPath: 'v2/users',
  },
};

export const useGetUsers = (
  participant: string,
  options?: UseQueryOptions<unknown, any, UserDto[]>,
  isRemote = false,
  apiVersion = API_VERSION_V1,
) => {
  return useQuery(
    [QueryKeys.USERS, participant, 'users'],
    async () => {
      const res = await useControllerService(isRemote).getRequest(
        participant,
        USERS_API_PATHS[apiVersion].getUsersPath,
      );
      return apiVersion === API_VERSION_V1 ? res.result : res.users;
    },
    { placeholderData: [], ...options },
  );
};

export const useCreateUser = (participant: string, isRemote = false, apiVersion = API_VERSION_V1) => {
  return useMutation(async (values: UserDtoWithAdminOption) => {
    if (apiVersion === API_VERSION_V1) {
      const { primaryParty, userId, rights } = values;
      const res = await useControllerService(isRemote).postRequest(
        participant,
        USERS_API_PATHS[apiVersion].createUserPath,
        {
          primaryParty,
          userId,
          rights,
        },
      );
      return res.result;
    }

    const reqRights = values.rights?.map(({ type, party }) => ({
      kind: {
        [type]: {
          value: { party },
        },
      },
    }));

    if (values.isParticipantAdmin) {
      reqRights.push({
        kind: {
          ParticipantAdmin: {
            value: {
              party: undefined,
            },
          },
        },
      });
    }

    if (values.isCanReadAsAnyParty) {
      reqRights.push({
        kind: {
          CanReadAsAnyParty: {
            value: {
              party: undefined,
            },
          },
        },
      });
    }

    if (values.isCanExecuteAsAnyParty) {
      reqRights.push({
        kind: {
          CanExecuteAsAnyParty: {
            value: {
              party: undefined,
            },
          },
        },
      });
    }

    if (values.isIdentityProviderAdmin) {
      reqRights.push({
        kind: {
          IdentityProviderAdmin: {
            value: {
              party: undefined,
            },
          },
        },
      });
    }

    const resValues = {
      user: {
        id: values.userId as string,
        primaryParty: values.primaryParty,
        isDeactivated: false,
        identityProviderId: '',
      },
      rights: reqRights ?? [],
    };

    const res = await useControllerService(isRemote).postRequest(
      participant,
      USERS_API_PATHS[apiVersion].createUserPath,
      resValues,
    );
    return res.result;
  });
};

export const useGetAuthUser = (participant: string, user: string, isRemote = false, apiVersion = API_VERSION_V1) => {
  return useQuery([QueryKeys.USERS, participant, 'users', user], async () => {
    const res = await useControllerService(isRemote).getRequest(
      participant,
      USERS_API_PATHS[apiVersion].getSpecifiedUserPath,
    );
    return res.result;
  });
};

export const useGetSpecificUser = (
  participant: string,
  user: string,
  isRemote = false,
  apiVersion = API_VERSION_V1,
) => {
  return useQuery([QueryKeys.USERS, participant, 'users', user], async () => {
    const res = await useControllerService(isRemote).postRequest(
      participant,
      USERS_API_PATHS[apiVersion].getSpecifiedUserPath,
      {
        userId: user,
      },
    );
    return res.result;
  });
};

export const useGetUserRights = (
  participant: string,
  userId: string,
  isRemote = false,
  apiVersion = API_VERSION_V1,
) => {
  if (apiVersion === API_VERSION_V1) {
    return useQuery<UserDto['rights']>([QueryKeys.USERS, participant, 'users', userId, 'rights'], async () => {
      const res = await useControllerService(isRemote).postRequest(participant, 'v1/user/rights', {
        userId,
      });
      return res.result;
    });
  }

  return useQuery([QueryKeys.USERS, participant, 'users', userId, 'rights'], async () => {
    const res = await useControllerService(isRemote).getRequest(participant, `v2/users/${userId}/rights`);
    const transformedRights = res.rights.map((r) => {
      const kind = Object.keys(r.kind)[0];
      const value = r.kind[kind];
      return {
        type: kind,
        party: value.value.party ?? '',
      };
    });
    return transformedRights;
  });
};

type GrantRightsInput = RightDto[];
type GrantRightsOutput = any; // Replace with actual response type if known

export const useGrantUserRights = (
  participant: string,
  userId: string,
  isRemote = false,
  apiVersion = API_VERSION_V1,
): UseMutationResult<GrantRightsOutput, Error, GrantRightsInput> => {
  const queryClient = useQueryClient();

  if (apiVersion === API_VERSION_V1) {
    return useMutation<GrantRightsOutput, Error, GrantRightsInput>(
      async (rights: RightDto[]) => {
        const res = await useControllerService(isRemote).postRequest(participant, 'v1/user/rights/grant', {
          userId,
          rights,
        });
        return res.result;
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries([QueryKeys.USERS, participant, 'users']);
        },
      },
    );
  }

  return useMutation<GrantRightsOutput, Error, GrantRightsInput>(
    async (rights: RightDto[]) => {
      const reqRights = rights?.map(({ type, party }) => ({
        kind: {
          [type]: {
            value: { party },
          },
        },
      }));
      const res = await useControllerService(isRemote).postRequest(participant, `v2/users/${userId}/rights`, {
        userId,
        rights: reqRights,
        identityProviderId: '',
      });
      return res.result;
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([QueryKeys.USERS, participant, 'users']);
      },
    },
  );
};

export const useRevokeUserRights = (
  participant: string,
  userId: string,
  isRemote = false,
  apiVersion = API_VERSION_V1,
): UseMutationResult<GrantRightsOutput, Error, GrantRightsInput> => {
  const queryClient = useQueryClient();

  if (apiVersion === API_VERSION_V1) {
    return useMutation<GrantRightsOutput, Error, GrantRightsInput>(
      async (values: RightDto[]) => {
        const res = await useControllerService(isRemote).postRequest(
          participant,
          USERS_API_PATHS[apiVersion].revokeUserRightsPath,
          {
            userId,
            rights: values,
          },
        );
        return res.result;
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries([QueryKeys.USERS, participant, 'users']);
        },
      },
    );
  }

  return useMutation<GrantRightsOutput, Error, GrantRightsInput>(
    async (rights: RightDto[]) => {
      const reqRights = rights?.map(({ type, party }) => ({
        kind: {
          [type]: {
            value: { party },
          },
        },
      }));
      const res = await useControllerService(isRemote).patchRequest(participant, `v2/users/${userId}/rights`, {
        userId,
        rights: reqRights,
        identityProviderId: '',
      });
      return res;
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([QueryKeys.USERS, participant, 'users']);
      },
    },
  );
};

export const useDeleteUser = (participant: string, isRemote = false, apiVersion = API_VERSION_V1) => {
  const queryClient = useQueryClient();

  if (apiVersion === API_VERSION_V1) {
    return useMutation(
      async (user: string) => {
        const res = await useControllerService(isRemote).postRequest(
          participant,
          USERS_API_PATHS[apiVersion].deleteUserPath,
          { userId: user },
        );
        return res.result;
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([QueryKeys.USERS, participant, 'users']);
        },
      },
    );
  }
  return useMutation(
    async (user: string) => {
      const res = await useControllerService(isRemote).deleteRequest(
        participant,
        `${USERS_API_PATHS[apiVersion].deleteUserPath}/${user}`,
      );
      return res.result;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.USERS, participant, 'users']);
      },
    },
  );
};
